import { del, get, post, put } from '@utilities';
import { type AxiosResponse } from 'axios';
import { withDerivedBuilderThickness } from '@utilities/builderThickness';
import { patch } from '@utilities';

const WALL_PANEL_LAYOUT_TYPES = new Set(['TOP_GAP', 'MID_CUT', 'OPTIMIZED']);
const WALL_PANEL_TYPES = new Set([
	'READY_MADE_FLUTED_PANELS',
	'DRYWALL_PANELING',
	'PLYWALL_PANELING'
]);
const DEFAULT_WALL_PANEL_TYPE = 'PLYWALL_PANELING';
const CUSTOM_FLUTED_PANEL_CODE = 'CUSTOM_FLUTED_PANEL';

const normalizeWallPanelToken = (value: any) =>
	String(value ?? '')
		.trim()
		.toUpperCase()
		.replace(/[^A-Z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');

const resolveWallPanelType = (value: any): string | null => {
	const normalized = normalizeWallPanelToken(value);
	return WALL_PANEL_TYPES.has(normalized) ? normalized : null;
};

const resolveWallPanelCncType = (value: any): string => {
	const normalized = normalizeWallPanelToken(value);
	if (!normalized || normalized === 'NONE') return 'NONE';
	if (normalized === 'CNC_GROOVE' || normalized === 'CNC_CUT_GRV') return 'CNC_CUT_GRV';
	if (
		normalized === 'CNC_DESIGN' ||
		normalized === 'CNC_2D_STRAIGHT' ||
		normalized === 'CNC_2D_ROUNDED' ||
		normalized === 'CNC_2D_FIGURINES' ||
		normalized === 'CNC_3D_DESIGN'
	) {
		return 'CNC_DESIGN';
	}
	return normalized;
};

const wallPanelBoolean = (value: any): boolean => {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
	return Boolean(value);
};

const hasWallPanelMaterialValue = (value: any): boolean => {
	if (!value) return false;
	if (typeof value !== 'object') return true;
	return Object.values(value).some((field) => field !== null && field !== undefined && field !== '');
};

const isCustomFlutedPanel = (value: any): boolean => {
	const code = normalizeWallPanelToken(value?.code ?? value?.materialCode ?? value?.value ?? value);
	return code === CUSTOM_FLUTED_PANEL_CODE;
};

const isStripLight = (value: any): boolean => {
	const haystack = [value?.code, value?.name, value?.type, value?.description].filter(Boolean).join(' ');
	return haystack.toUpperCase().includes('STRIP');
};

const isPuOrDucoFinish = (value: any): boolean => {
	const haystack = [value?.code, value?.materialCode, value?.name, value?.description].filter(Boolean).join(' ');
	return /(^|[^A-Z0-9])(PU|PUPA|DUCO|DECO|DEPA)([^A-Z0-9]|$)/.test(haystack.toUpperCase());
};

const normalizeWallPanelPayload = <T extends Record<string, any>>(payload: T): T => {
	const orientation = normalizeWallPanelToken(payload?.orientation);
	const panelingType = normalizeWallPanelToken(payload?.panelingType ?? payload?.paneling_type);
	const legacyWallPanelType = resolveWallPanelType(payload?.wallPanelType ?? payload?.wall_panel_type);
	const typeFromLegacyPaneling = resolveWallPanelType(payload?.panelingType ?? payload?.paneling_type);
	const nextOrientation = orientation === 'HORIZONTAL' ? 'HORIZONTAL' : 'VERTICAL';
	const nextPanelingType =
		nextOrientation === 'HORIZONTAL' && panelingType === 'TOP_GAP'
			? 'OPTIMIZED'
			: WALL_PANEL_LAYOUT_TYPES.has(panelingType)
				? panelingType
				: 'OPTIMIZED';
	const nextWallPanelType = legacyWallPanelType ?? typeFromLegacyPaneling ?? DEFAULT_WALL_PANEL_TYPE;
	const isFluted = nextWallPanelType === 'READY_MADE_FLUTED_PANELS';
	const isPlywall = nextWallPanelType === 'PLYWALL_PANELING';
	const nextCncType = resolveWallPanelCncType(payload?.cncType ?? payload?.cnc_type);
	const lightingEnabled = wallPanelBoolean(payload?.lightingEnabled ?? payload?.lighting_enabled ?? payload?.backLight ?? payload?.back_light);
	const lightingMaterial = payload?.lightingMaterial ?? payload?.lighting_material;
	const panelOuterFinish = isPlywall ? payload?.panelOuterFinish ?? payload?.panel_outer_finish : null;
	const flutedPanelMaterial = payload?.flutedPanelMaterial ?? payload?.fluted_panel_material;
	const flutedCustomMaterial = payload?.flutedCustomMaterial ?? payload?.fluted_custom_material;
	const explicitFlutedMaterial =
		hasWallPanelMaterialValue(flutedPanelMaterial) && !isCustomFlutedPanel(flutedPanelMaterial) ? flutedPanelMaterial : null;
	const explicitCustomFluted = hasWallPanelMaterialValue(flutedCustomMaterial) ? flutedCustomMaterial : null;
	const flatCustomFluted =
		payload?.flutedCustomName ||
		payload?.flutedCustomThickness ||
		payload?.flutedCustomLength ||
		payload?.flutedCustomWidth ||
		payload?.flutedCustomCost
			? {
					name: payload?.flutedCustomName,
					thickness: payload?.flutedCustomThickness,
					length: Number(payload?.flutedCustomLength) || 0,
					width: Number(payload?.flutedCustomWidth) || 0,
					basePrice: Number(payload?.flutedCustomCost) || 0,
					unit: 'panel'
				}
			: null;

	return {
		...payload,
		orientation: nextOrientation,
		panelingType: nextPanelingType,
		wallPanelType: nextWallPanelType,
		cncType: isFluted ? 'NONE' : nextCncType,
		cncArea: isFluted ? 0 : payload?.cncArea ?? payload?.cnc_area ?? 0,
		backLight: lightingEnabled,
		lightingEnabled,
		lightingMaterial: isStripLight(lightingMaterial) ? lightingMaterial : null,
		boxPaneling: isFluted ? false : wallPanelBoolean(payload?.boxPaneling ?? payload?.box_paneling),
		boxPanelingType: payload?.boxPanelingType ?? payload?.box_paneling_type ?? 'PLY_STYLE',
		boxPanelingDepth: isFluted ? 0 : payload?.boxPanelingDepth ?? payload?.box_paneling_depth ?? 0,
		boxPanelingRunningFeet: isFluted ? 0 : payload?.boxPanelingRunningFeet ?? payload?.box_paneling_running_feet ?? 0,
		boxPanelingMaterial: isFluted || isPlywall ? null : payload?.boxPanelingMaterial ?? payload?.box_paneling_material ?? null,
		boxPanelingPrice: isFluted ? 0 : payload?.boxPanelingPrice ?? payload?.box_paneling_price ?? 0,
		panelBoard: isPlywall ? payload?.panelBoard ?? payload?.panel_board : null,
		panelInnerFinish:
			isPlywall && isPuOrDucoFinish(panelOuterFinish)
				? payload?.panelInnerFinish ?? payload?.panel_inner_finish
				: null,
		panelOuterFinish,
		flutedPanelMaterial: isFluted ? explicitFlutedMaterial : null,
		flutedCustomMaterial: isFluted && !explicitFlutedMaterial ? explicitCustomFluted ?? flatCustomFluted : null
	};
};

/** Jackson cannot coerce "" to Materials / List<Materials>; send null instead. */
const MATERIAL_OBJECT_KEYS = new Set([
	'carcassCore',
	'carcassInnerFinish',
	'carcassOuterFinish',
	'backPanel',
	'shutterCore',
	'shutterInnerFinish',
	'shutterOuterFinish',
	'handles',
	'handleCap',
	'kitchenSink',
	'faucet',
	'mwoModel',
	'fabric',
	'liningFabric',
	'sheerFabric',
	'trackAccessory',
	'bracketAccessory',
	'motorAccessory',
	'panelBoard',
	'panelInnerFinish',
	'panelOuterFinish',
	'profileLightMaterial',
	'boxPanelingMaterial',
	'lightingMaterial',
	'flutedPanelMaterial',
	'flutedCustomMaterial',
	'core',
	'innerFinish',
	'outerFinish',
	'fixingMechanism',
	'coilType',
	'coreMaterial',
	'subCategory',
	'material'
]);

const MATERIAL_LIST_KEYS = new Set([
	'hardwareMaterials',
	'hardwareConnectors',
	'hardware_connectors',
	'cabinetFixtures',
	'lightingMaterials'
]);

const sanitizeMaterialValue = (value: any): any => {
	if (value == null || value === '') return null;
	if (typeof value !== 'object') return null;
	if (Array.isArray(value)) {
		return value.map(sanitizeMaterialValue).filter(Boolean);
	}
	return value;
};

export const sanitizeBuilderMaterialFields = <T extends Record<string, any>>(payload: T): T => {
	if (!payload || typeof payload !== 'object') return payload;
	const next: Record<string, any> = withDerivedBuilderThickness(payload);
	Object.keys(next).forEach(key => {
		if (MATERIAL_OBJECT_KEYS.has(key) || MATERIAL_LIST_KEYS.has(key)) {
			next[key] = sanitizeMaterialValue(next[key]);
		}
		// exposedPanel is List<String>, not Materials — still reject ""
		if (key === 'exposedPanel' && next[key] === '') {
			next[key] = null;
		}
	});
	return next as T;
};

export const updateCabinetState = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await put('builder/cabinet/state', sanitizeBuilderMaterialFields(data));
};

export const fetchCabinetState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/cabinet/${productId}/state`);
};

export const fetchCabinetCutlist = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/cabinet/cutlist/${productId}`);
};

export const calculateFreeformCabinet = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('builder/freeform/calculate', data);

export const previewFreeformCabinet = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('builder/freeform/price-preview', data);

export const saveFreeformCabinet = async (productId: string, data: Record<string, any>): Promise<AxiosResponse> =>
	await put(`builder/freeform/${productId}`, data);

export const fetchFreeformCabinet = async (productId: string): Promise<AxiosResponse> =>
	await get(`builder/freeform/${productId}`);

export const listFreeformTemplates = async (): Promise<AxiosResponse> => await get('builder/freeform/templates');
export const fetchFreeformTemplate = async (id: string): Promise<AxiosResponse> => await get(`builder/freeform/templates/${encodeURIComponent(id)}`);
export const createFreeformTemplate = async (data: Record<string, any>): Promise<AxiosResponse> => await post('builder/freeform/templates', data);
export const deleteFreeformTemplate = async (id: string): Promise<AxiosResponse> => await del(`builder/freeform/templates/${encodeURIComponent(id)}`);

export const fetchFreeformHardwareRules = async (): Promise<AxiosResponse> =>
	await get('builder/freeform/hardware-rules');

export const saveFreeformHardwareRule = async (materialCode: string, ruleType: string, data: Record<string, any>): Promise<AxiosResponse> =>
	await put(`builder/freeform/hardware-rules/${encodeURIComponent(materialCode)}/${encodeURIComponent(ruleType)}`, data);

export const updateCabinetCutlist = async (productId: string, data: any): Promise<AxiosResponse> => {
	return await put(`builder/cabinet/cutlist/${productId}`, Array.isArray(data) ? { cutlistItems: data } : data);
};

export const getCabinetCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/cabinet/calculate`, sanitizeBuilderMaterialFields(data));
};

export const calculateCabinetBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/cabinet/calculate`, sanitizeBuilderMaterialFields(payload));
};

export const getCabinetHardwareKits = async (params: Record<string, any> = {}): Promise<AxiosResponse> => {
	const query = new URLSearchParams(
		Object.entries(params)
			.filter(([, value]) => value !== undefined && value !== null && value !== '')
			.map(([key, value]) => [key, String(value)])
	).toString();
	return await get(`builder/cabinet/hardware-kits${query ? `?${query}` : ''}`);
};

export const saveCabinetHardwareKit = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/cabinet/hardware-kits`, payload);
};

export const updateCabinetMaterialsInBulk = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/cabinet/bulk-material-update`, sanitizeBuilderMaterialFields(payload));
};

export const updateModularMaterialsInBulk = async (kind: 'wardrobe' | 'loft', payload: Record<string, unknown>): Promise<AxiosResponse> =>
	post(`${kind}/bulk-material-update`, sanitizeBuilderMaterialFields(payload));

export const getCounterTopCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/v2/countertop/calculate`, data);
};

export const calculateCounterTopBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/v2/countertop/calculate`, payload);
};

export const fetchLegacyCounterTopState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/countertop/${productId}`);
};

export const fetchLoftState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`loft/state/${productId}`);
};

export const fetchLoftCutlist = async (productId: string): Promise<AxiosResponse> => {
	return await get(`loft/cutlist/${productId}`);
};

export const calculateLoftBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('loft/calculate', sanitizeBuilderMaterialFields(payload));
};

export const fetchCounterTopState = async (productId: string, quotationId?: string): Promise<AxiosResponse> => {
	const query = quotationId ? `?quotationId=${encodeURIComponent(quotationId)}` : '';
	return await get(`builder/v2/countertop/${productId}${query}`);
};

export const getBuilderApiErrorMessage = (error: unknown, fallback: string): string => {
	if (typeof error === 'string') return error;
	if (error && typeof error === 'object') {
		const message = (error as any).message ?? (error as any).response?.data?.message;
		if (typeof message === 'string' && message.trim()) return message;
	}
	return fallback;
};

export const isCountertopStateNotFound = (error: unknown): boolean =>
	getBuilderApiErrorMessage(error, '').toLowerCase().includes('countertop state not found');

export const saveQuotationCountertopTransportation = async (quotationId: string, amount: number | null): Promise<AxiosResponse> => {
	return await put(`builder/v2/countertop/quotation/${encodeURIComponent(quotationId)}/transportation`, { amount });
};

export const fetchQuotationCountertopStates = async (quotationId: string): Promise<AxiosResponse> => {
	return await get(`builder/v2/countertop/quotation/${encodeURIComponent(quotationId)}`);
};

export const fetchFCBuilderData = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/fc/${productId}`);
};

export const calculateFCBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/fc/calculate`, payload);
};

export const fetchWallPanelState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/wall-panel/${productId}`);
};

export const getWallPanelCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/wall-panel/calculate`, sanitizeBuilderMaterialFields(normalizeWallPanelPayload(data)));
};

export const calculateWallPanelBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/wall-panel/calculate`, sanitizeBuilderMaterialFields(normalizeWallPanelPayload(payload)));
};

export const fetchProfileLightState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/profile-light/${productId}`);
};

export const calculateProfileLightBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/profile-light/calculate`, sanitizeBuilderMaterialFields(payload));
};

export const fetchWardrobeCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`wardrobe/calculate-with-cutlist`, sanitizeBuilderMaterialFields(data));
};

export const calculateWardrobeBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`wardrobe/calculate`, sanitizeBuilderMaterialFields(payload));
};

export const fetchWardrobeState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`wardrobe/state/${productId}`);
};

export const fetchWardrobeCutlist = async (productId: string): Promise<AxiosResponse> => {
	return await get(`wardrobe/cutlist/${productId}`);
};

export const getElectricalCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`builder/electrical/calculate`, data);
};

export const getProjectElectricalState = async (projectId: string): Promise<AxiosResponse> => {
	return await get(`builder/electrical/project/${projectId}`);
};

export const updateElectricalState = async (productId: string, data: any): Promise<AxiosResponse> => {
	return await put(`builder/electrical/${productId}`, data);
};

export const getPaintBuilderState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`paint/${productId}/state`);
};

export const getPaintV2BuilderState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`paint/v2/state/${productId}`);
};

export const getPaintBuilderCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`paint/calculate`, data);
};

export const calculatePaintBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('paint/calculate', payload);
};

export const calculatePaintV2Builder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('paint/v2/calculate', payload);
};

export const getAllBuilderStates = async (quotationId: string): Promise<AxiosResponse> => {
	return await get(`quotation/${quotationId}/builder-states`);
};

export const getWallMouldingState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`moulding/${productId}/state`);
};

export const fetchWallMouldingBuilderState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`moulding/${productId}/state`);
};

export const getWallMouldingCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`moulding/calculate`, data);
};

export const calculateWallMouldingBuilder = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`moulding/calculate`, data);
};

export const fetchDrawerState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`drawer/${productId}/state`);
};

export const fetchDrawerCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`drawer/calculate`, sanitizeBuilderMaterialFields(data));
};

export const fetchDrawerCutlist = async (productId: string): Promise<AxiosResponse> => {
	return await get(`drawer/cutlist/${productId}`);
};

export const calculateDrawerBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`drawer/calculate`, sanitizeBuilderMaterialFields(payload));
};

export const fetchLedgeState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`ledge/state/${productId}`);
};

export const fetchLedgeCalculation = async (data: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`ledge/calculate`, sanitizeBuilderMaterialFields(data));
};

export const fetchLedgeCutlist = async (productId: string): Promise<AxiosResponse> => {
	return await get(`ledge/cutlist/${productId}`);
};

export const calculateLedgeBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post(`ledge/calculate`, sanitizeBuilderMaterialFields(payload));
};

export const updatePreDesignBuilder = async (
	productId: string,
	payload: {
		budgetType: string;
		price: number;
		areaName: string;
		projectId: number;
		floor: number;
	}
): Promise<AxiosResponse> => {
	return await put(`/pre-design/update/${productId}`, payload);
};

export const fetchCurtainState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`curtain/${productId}/state`);
};

/** Shared builder rules used by the parametric editor and cutlist previews. */
export const fetchBuilderRules = async (builderCode: string, version?: number): Promise<AxiosResponse> => {
	return await get(`builder/rules/${encodeURIComponent(builderCode)}`, version == null ? undefined : { version });
};

export const calculateProfileProductBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('builder/profile-product/calculate', payload);
};

export const fetchProfileProductState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/profile-product/state/${encodeURIComponent(productId)}`);
};

export const fetchProfileProductCutlist = async (productId: string): Promise<AxiosResponse> => {
	return await get(`builder/profile-product/cutlist/${encodeURIComponent(productId)}`);
};

export const previewCabinetBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('builder/cabinet/preview', payload);
};

export const previewWardrobeBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('wardrobe/preview', payload);
};

export const calculateCurtainBuilder = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	const cutoutType = payload.cutoutType ?? payload.cutout_type ?? 'WINDOW';
	const hardwareMountType = payload.hardwareMountType ?? payload.hardware_mount_type ?? 'TRACK';
	const liningType = payload.liningType ?? payload.lining_type ?? 'NORMAL';
	return await post(
		`curtain/calculate`,
		sanitizeBuilderMaterialFields({
			...payload,
			cutoutType,
			cutout_type: cutoutType,
			hardwareMountType,
			hardware_mount_type: hardwareMountType,
			liningType,
			lining_type: liningType
		})
	);
};

export const fetchCostingSyncStatus = async (quotationId: string): Promise<AxiosResponse> => {
	return await get(`quotation/${quotationId}/costing-sync-status`);
};

/** Recalculate modular quote lines from saved builder state (same as ERP “Refresh costing”). */
export const refreshQuotationCosting = async (quotationId: string): Promise<AxiosResponse> => {
	return await post(`quotation/${quotationId}/refresh-costing`, {});
};

export const fetchCurtainBuilderState = async (productId: string): Promise<AxiosResponse> => {
	return await get(`curtain/${productId}/state`);
};

export const fetchProjectPaintScheme = async (projectId: string | number, quotationId?: string): Promise<AxiosResponse> =>
	await get(`paint/projects/${projectId}/scheme${quotationId ? `?quotationId=${encodeURIComponent(quotationId)}` : ''}`);

export const updateProjectPaintScheme = async (projectId: string | number, data: Record<string, any>): Promise<AxiosResponse> =>
	await patch(`paint/projects/${projectId}/scheme`, data);

export const fetchPaintCatalogBrands = async (): Promise<AxiosResponse> => await get('paint/catalog/brands');

export const fetchPaintCatalogColors = async (brandCode: string, query?: string): Promise<AxiosResponse> =>
	await get(`paint/catalog/brands/${encodeURIComponent(brandCode)}/colors${query ? `?query=${encodeURIComponent(query)}` : ''}`);

export const fetchNearestPaintColors = async (brandCode: string, hex: string): Promise<AxiosResponse> =>
	await post(`paint/catalog/brands/${encodeURIComponent(brandCode)}/nearest`, { hex });

export const updateProjectPaintBrand = async (projectId: string | number, data: Record<string, any>): Promise<AxiosResponse> =>
	await put(`paint/projects/${projectId}/brand`, data);

export const fetchPreDesignBuilderStates = async (projectId: string | number): Promise<AxiosResponse> => {
	return await get(`pre-design/${projectId}/builder-states`);
};

export const addPreDesignProduct = async (payload: Record<string, any>): Promise<AxiosResponse> => {
	return await post('/pre-design/product', payload);
};

export const removePreDesignProduct = async (
	productId: string,
	projectId: string | number
): Promise<AxiosResponse> => {
	return await del(`/pre-design/product/${productId}`, { params: { projectId } });
};
